import React, { useState, useEffect, useRef } from 'react';
import { useField } from 'formik';
import { TextField, Autocomplete, CircularProgress, InputAdornment } from '@mui/material';
import { debounce } from 'lodash';

interface FormikTextFieldProps {
  name: string;
  label?: string;
  type?: string;
  placeholder?: string;
  className?: string;
  fieldToSearch?: string;
  onSuggestionSearch?: (fieldName: string, searchTerm: string) => Promise<string[]>;
  disabled?: boolean;
  step?: string | number;
  min?: string | number;
  max?: string | number;
  startAdornment?: React.ReactNode;
}

export const FormikTextField: React.FC<FormikTextFieldProps> = ({
  name,
  label,
  type = 'text',
  placeholder,
  className,
  fieldToSearch,
  onSuggestionSearch,
  disabled = false,
  step,
  min,
  max,
  startAdornment,
}) => {
  const [field, meta, helpers] = useField(name);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const debouncedSearch = useRef(
    fieldToSearch && onSuggestionSearch
      ? debounce(async (searchValue: string) => {
          if (searchValue.length >= 1) {
            setLoading(true);
            try {
              const results = await onSuggestionSearch(fieldToSearch, searchValue);
              setSuggestions(results);
            } catch (error) {
              console.error('Search error:', error);
              setSuggestions([]);
            }
            setLoading(false);
          } else {
            setSuggestions([]);
          }
        }, 300)
      : null
  ).current;

  useEffect(() => {
    if (debouncedSearch && field.value) {
      debouncedSearch(field.value);
    }
  }, [field.value]);

  useEffect(() => {
    return () => {
      if (debouncedSearch) {
        debouncedSearch.cancel();
      }
    };
  }, []);

  if (fieldToSearch && onSuggestionSearch) {
    return (
      <Autocomplete
        freeSolo
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={suggestions}
        loading={loading}
        disabled={disabled}
        value={field.value || ''}
        onChange={(_, newValue) => {
          helpers.setValue(newValue || '');
        }}
        onInputChange={(_, newInputValue) => {
          helpers.setValue(newInputValue);
        }}
        renderInput={params => (
          <TextField
            {...params}
            name={name}
            label={label}
            type={type}
            placeholder={placeholder}
            error={meta.touched && Boolean(meta.error)}
            helperText={meta.touched && meta.error}
            className={className}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }

  return (
    <TextField
      fullWidth={label ? true : false}
      name={name}
      label={label}
      type={type}
      placeholder={placeholder}
      disabled={disabled}
      value={field.value || ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={meta.touched && Boolean(meta.error)}
      helperText={meta.touched && meta.error}
      className={className}
      InputProps={{
        startAdornment: startAdornment ? (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ) : undefined,
      }}
      inputProps={{
        step: step,
        min: min,
        max: max,
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (type === 'number' && !/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== '.') {
            e.preventDefault();
          }
        },
      }}
    />
  );
};
